import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Button, CircularProgress, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import { useNavigate } from "react-router-dom";
import reportServices from "app/services/automations/report-services";
import generatedFileServices, { requestErrors, waitUntilReady } from "app/services/automations/generated-file-services";
import ReportDateRange, { todayIso } from "../../ReportDateRange";
import TemperatureSheetFields, { DEFAULT_PARAMS, canBuild, prepareParams } from "./TemperatureSheetFields";
import TemperatureSheetPreview from "./TemperatureSheetPreview";

// Planilla de temperaturas: un equipo por fila y una columna por hora o por turno. Lo que se configura aquí se puede
// guardar como plantilla para generarla después eligiendo solo las fechas.
const TemperatureSheetReport = ({ type, stores }) => {
    const navigate = useNavigate();
    const today = todayIso();
    const [form, setForm] = useState({ store_id: stores[0]?.id || "", from: today, to: today, ...DEFAULT_PARAMS });
    const [options, setOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        if (!form.store_id && stores.length) setForm((current) => ({ ...current, store_id: stores[0].id }));
    }, [stores, form.store_id]);

    useEffect(() => {
        if (!form.store_id) return;
        setLoadingOptions(true);
        setPreview(null);
        reportServices.getOptions(type.key, form.store_id)
            .then((data) => {
                setOptions(data);
                setForm((current) => prepareParams(data, { ...current, device_ids: [] }));
            })
            .catch(() => setOptions(null))
            .finally(() => setLoadingOptions(false));
    }, [form.store_id, type.key]);

    const update = (patch) => {
        setForm((current) => ({ ...current, ...patch }));
        setPreview(null);
        setNotice(null);
    };

    const ready = !loadingOptions && canBuild(options, form);

    const showPreview = async () => {
        setPreviewing(true);
        setError(null);
        try {
            setPreview(await reportServices.preview(type.key, form));
        } catch (requestError) {
            setError(requestErrors(requestError, "No se pudo armar la vista previa."));
        } finally {
            setPreviewing(false);
        }
    };

    const downloadPdf = async () => {
        setGenerating(true);
        setError(null);
        setNotice(null);
        try {
            const requested = await generatedFileServices.requestReport(type.key, form);
            const file = await waitUntilReady(requested.id);
            await generatedFileServices.download(file);
            setNotice("Listo. El PDF también quedó en Descargas.");
        } catch (requestError) {
            setError(requestError?.response ? requestErrors(requestError, "No se pudo generar el PDF.") : requestError.message);
        } finally {
            setGenerating(false);
        }
    };

    // Abre el editor de plantillas con esta configuración (sin las fechas, que se eligen al generar).
    const saveAsTemplate = () => {
        const { store_id: storeId, from, to, ...params } = form;
        navigate("/automations/reports/templates/new", {
            state: { draft: { report_type: type.key, store_id: storeId, params }, from: "/automations/reports?tab=quick" },
        });
    };

    if (!stores.length) return <Alert severity="info">Todavía no tienes locales con dispositivos.</Alert>;

    return (
        <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                <Typography variant="h4" sx={{ mb: 0.5 }}>{type.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{type.description}</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Autocomplete
                            options={stores} disableClearable
                            value={stores.find((store) => `${store.id}` === `${form.store_id}`) || null}
                            getOptionLabel={(option) => option?.name || ""}
                            onChange={(event, value) => update({ store_id: value?.id || "" })}
                            renderInput={(inputParams) => <TextField {...inputParams} label="Local" />}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ReportDateRange from={form.from} to={form.to} minDate={options?.earliest_date} onChange={update} />
                    </Grid>
                </Grid>
                <TemperatureSheetFields options={options} value={form} onChange={update} />

                {!!error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {!!notice && <Alert severity="success" sx={{ mt: 2 }}>{notice}</Alert>}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                    <Button variant="outlined" startIcon={previewing ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon />}
                            disabled={previewing || generating || !ready} onClick={showPreview}>
                        Vista previa
                    </Button>
                    <Button variant="contained" startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
                            disabled={generating || !ready} onClick={downloadPdf}>
                        {generating ? "Generando el PDF…" : "Descargar PDF"}
                    </Button>
                    <Button startIcon={<BookmarkAddOutlinedIcon />} disabled={!ready} onClick={saveAsTemplate}>
                        Guardar como plantilla
                    </Button>
                </Stack>
            </Paper>

            {preview && <TemperatureSheetPreview data={preview} />}
        </Stack>
    );
};

export default TemperatureSheetReport;
